import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useZoomDetection } from "common/hooks";

const standardButtonVariants = cva(
  " px-4 py-2 min-h-[44px] inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Primary action buttons
        primary: "bg-primary-800 text-white rounded-[8px] gap-2",

        primaryNew:
          "bg-bg-brand-primary-default text-white rounded-[8px] gap-2",

        // Secondary action buttons
        secondary:
          "bg-container-brand-minimal border border-border-token-brand-minimal text-fg-neutral-strong rounded-[8px]",

        // Login buttons
        loginbutton:
          "bg-container-brand-minimal border border-border-token-brand-minimal text-fg-neutral-strong rounded-[8px]",

        // Outline buttons
        outline: "border border-input bg-background rounded-[8px]",

        // Ghost/transparent buttons
        ghost: "rounded-[8px]",

        // Link-style buttons
        link: "text-fg-neutral-strong underline-offset-4",

        blueLink:
          "!px-0 label-m-medium text-fg-brand-subtle underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50",

        // Destructive/danger buttons
        destructive: "bg-error-900 text-white rounded-[8px]",

        // Tag-style buttons (rounded pills)
        tag: "rounded-[9999px]",

        // Navigation/edit link buttons
        edit: "text-fg-brand-subtle",

        // Venue action buttons (like share, note)
        venue:
          "text-blue-950 bg-white !rounded-[8px] border text-center flex items-center gap-2 body-l-semibold bg-container-brand-minimal border-border-token-brand-minimal",

        venueNew:
          "border border-border-token-brand-minimal rounded-lg px-4 py-2.5 flex gap-2 items-center hover:bg-bg-brand-ghost-hover transition-colors",

        // Back/navigation buttons (absolute positioned)
        back: "!absolute !top-1/2 transform -translate-y-1/2 !left-4 !px-0 !py-0 !w-auto !h-auto",

        // Filter/sort buttons
        filter:
          "flex items-center gap-2 bg-bg-brand-secondary-default rounded-[8px] text-center !h-[46px]",

        // Orange/warning buttons
        warning:
          "border border-orange-300 bg-orange-300 rounded-[8px] text-blue-950",

        // Icon-only buttons
        icon: "p-2 rounded-[8px]",

        // Text-only buttons (for character counts, labels, etc.)
        text: "text-fg-neutral-subtle",

        // Back/navigation buttons (absolute positioned)
        backListView: "!px-0 !py-0 !w-auto !h-auto",

        removeImage: "!w-auto !h-auto !px-0 !py-0 !w-auto !h-auto",

        closeModalZero:
          "!px-0 !py-0 !w-auto !h-auto !absolute !top-[-10px] !left-0",

        closeModal: "!w-auto !h-auto !px-0 !py-0",
      },
      size: {
        xs: "px-3 py-2 label-s-medium",
        sm: "px-3 py-2 label-s-medium",
        md: "px-4 py-2 label-m-medium",
        lg: "px-4 py-2 label-m-semibold",
        xlg: "px-4 py-2 label-l-bold",
        xxlg: "px-4 py-2 label-l-bold",
        icon: "w-4 h-4",
        "icon-sm": "w-3 h-3",
        "icon-lg": "w-5 h-5",
        "icon-xlg": "w-6 h-6",
        "icon-xxlg": "w-8 h-8",
        auto: "", // No size constraints
      },
      fullWidth: {
        true: "w-full",
        false: "w-auto",
      },
      position: {
        static: "static",
        absolute: "absolute",
        relative: "relative",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
      position: "static",
    },
  },
);

interface StandardButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof standardButtonVariants> {
  loading?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  imageSrc?: string;
  imageAlt?: string;
  imageContainerClassName?: string;
  imageContainer?: boolean;
}

const StandardButton = React.forwardRef<HTMLButtonElement, StandardButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      position,
      loading,
      disabled,
      children,
      icon,
      iconPosition = "left",
      imageSrc,
      imageAlt = "",
      imageContainer = false,
      imageContainerClassName,
      ...props
    },
    ref,
  ) => {
    const isZoomedIn = useZoomDetection();
    const isDisabled = disabled || loading;

    // Handle the case where only an icon or image is provided (icon-only buttons)
    const isIconOnly = (icon || imageSrc) && !children;
    const finalSize = isIconOnly && size === "md" ? "icon" : size;

    // Helper function to determine image size based on button size
    // These sizes are proportional to the button container sizes defined above
    const getImageSize = () => {
      const imageSizeMap = {
        xs: "w-3 h-3",
        sm: "w-3 h-3",
        md: "w-4 h-4",
        lg: "w-5 h-5",
        xlg: "w-6 h-6",
        xxlg: "w-8 h-8",

        icon: "w-4 h-4",
        "icon-sm": "w-3 h-3",
        "icon-lg": "w-5 h-5",
        "icon-xlg": "w-6 h-6",
        "icon-xxlg": "w-8 h-8",
      };

      return imageSizeMap[finalSize as keyof typeof imageSizeMap] || "w-4 h-4";
    };

    // Determine what to render (icon, image, or neither)
    const renderIcon = () => {
      if (loading) return null;

      if (imageSrc) {
        const imageSize = getImageSize();

        return imageContainer ? (
          <div
            className={cn(
              imageSize,
              imageContainerClassName,
              "shrink-0 min-w-[1.5rem] flex items-center justify-center",
            )}
          >
            <img
              src={imageSrc}
              alt={imageAlt}
              className={cn(imageSize, "shrink-0 min-w-[1.5rem]")}
              aria-hidden={!imageAlt}
            />
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={imageAlt}
            className={cn(
              imageSize,
              imageContainerClassName,
              "shrink-0 min-w-[1.5rem]",
            )}
            aria-hidden={!imageAlt}
          />
        );
      }

      return icon;
    };

    const iconElement = renderIcon();

    // Warn if icon-only button is missing aria-label (runs in all environments to catch a11y issues)
    if (isIconOnly && !props["aria-label"]) {
      console.error(
        "StandardButton: Icon-only button is missing aria-label. Please add an accessible name for screen readers.",
        { variant, icon: !!icon, imageSrc: !!imageSrc }
      );
    }

    return (
      <button
        className={cn(
          "flex flex-row items-center",
          standardButtonVariants({
            variant,
            size: finalSize,
            fullWidth,
            position,
            className,
          }),
          // Handle zoom-specific styling for tag variant
          variant === "tag" && "px-[16px] py-[8px]",
          variant === "loginbutton" &&
            (!isZoomedIn ? "flex-row text-left" : "flex-col text-center"),
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="m12 2l4 4h-3v5h-2V6H8l4-4z"
            />
          </svg>
        )}

        {/* Icon/Image positioning */}
        {iconElement && iconPosition === "left" && !loading && (
          <span
            className={cn(
              children ? "" : "",
              "flex-shrink-0 min-w-[1.5rem] flex items-center justify-center",
            )}
          >
            {iconElement}
          </span>
        )}

        {children}

        {iconElement && iconPosition === "right" && !loading && (
          <span
            className={cn(
              children ? "" : "",
              "flex-shrink-0 min-w-[1.5rem] flex items-center justify-center",
            )}
          >
            {iconElement}
          </span>
        )}
      </button>
    );
  },
);

StandardButton.displayName = "StandardButton";

export { StandardButton, standardButtonVariants, type StandardButtonProps };
